import { useCallback, useEffect, useMemo, useState } from 'react'

import { notFound, useNavigate } from '@tanstack/react-router'
import { useFormData } from 'brief-form'
import { routes } from 'commons/constants/routes'
import {
  useGetSalesDataSourceWebPage,
  useSalesDataClientSourceApprove,
  useSalesDataSourceSaveInternalNotes
} from 'commons/hooks/bank/salesData'
import { SalesDataSourceWebPage, SalesDataSourceWebPagePayload } from 'commons/types/DTO/bank/salesDataSourceWebPage'
import { TablePagination, Toast } from 'ui/components'
import { useConfirmation } from 'ui/hooks'

import { handlerServerError } from '@orbit/errors'

export const useSourceDetails = (sourceId: number) => {
  const navigate = useNavigate()
  const [isShowRejectDialog, setIsShowRejectDialog] = useState(false)
  const [webPageData, setWebPageData] = useState<SalesDataSourceWebPage | undefined>(undefined)
  const { config, Field } = useFormData<{ notes: string }>({ initialValue: { notes: '' } })
  const [filter, setFilter] = useState(new SalesDataSourceWebPagePayload(+sourceId))
  const { data, isFetched, isFetching, invalidate } = useGetSalesDataSourceWebPage(filter)

  useEffect(() => {
    if (isFetched) {
      if (data) {
        setWebPageData(data)
      } else {
        throw notFound()
      }
    }
  }, [isFetched, data])

  useEffect(() => {
    if (webPageData) {
      config.onChange({ notes: webPageData.infoInternalNotes }, config.errors || {})
    }
  }, [webPageData?.infoInternalNotes])

  const { routine: saveInternalNotes, isLoading: isInternalNotesSubmitting } = useSalesDataSourceSaveInternalNotes({
    onSuccess: async () => {
      invalidate()
      Toast.successSmall('Internal Notes has been saved.')
    },
    onError: (error) => handlerServerError(error)
  })

  const { routine: approve, isLoading: isApproving } = useSalesDataClientSourceApprove({
    onSuccess: async () => {
      invalidate()
      Toast.successSmall('Report has been recalculated with new Sales Data.')
      if (webPageData?.infoReportId) {
        if (webPageData?.reportType === 'custom') {
          navigate({ to: routes.bank.hrr.details, params: { id: webPageData.infoReportId.toString() } })
        } else {
          navigate({ to: '/mr/$id', params: { id: webPageData?.infoReportId.toString() } })
        }
      }
    },
    onError: (error) => handlerServerError(error)
  })

  const { open: openApproveConfirm, Confirmation: ApproveConfirmation } = useConfirmation({
    message: `Are you sure you want to proceed? Report will be recalculated.`,
    onConfirm: () => approve(sourceId),
    confirmationButtonText: 'Confirm',
    isConfirmationNegative: false
  })

  const showRejectDialog = useCallback(() => setIsShowRejectDialog(true), [])
  const hideRejectDialog = useCallback(() => setIsShowRejectDialog(false), [])

  const historyPagination: TablePagination = useMemo(
    () => ({
      total: webPageData?.historyTotalCount,
      page: filter.historyOffset / filter.historyLimit + 1,
      size: filter.historyLimit,
      onChange: (page: number, size: number) =>
        setFilter({ ...filter, historyOffset: (page - 1) * size, historyLimit: size })
    }),
    [filter, webPageData]
  )

  return {
    webPageData,
    invalidate,
    isFetching,
    config,
    Field,
    saveInternalNotes: () => saveInternalNotes({ id: sourceId, internalNotes: config.value.notes }),
    isInternalNotesSubmitting,
    ApproveConfirmation,
    approve: () => openApproveConfirm(),
    isApproving,
    isShowRejectDialog,
    showRejectDialog,
    hideRejectDialog,
    historyPagination
  }
}
